import { useState } from 'react';
import Header from './Header';
import './PrivacyRequestPage.css';

const REQUEST_TYPES = [
  { value: 'export', label: 'Export my data' },
  { value: 'delete', label: 'Delete my data' },
  { value: 'access', label: 'Access request' },
  { value: 'correction', label: 'Correct my data' }
];

function getPrivacyApiBase() {
  const explicitBase = process.env.REACT_APP_API_URL;
  if (explicitBase) return explicitBase.replace(/\/$/, '');

  const venueHost = process.env.REACT_APP_VENUE_API_HOST;
  if (venueHost) return `${venueHost.replace(/\/$/, '')}/api`;

  return '';
}

export default function PrivacyRequestPage() {
  const [form, setForm] = useState({
    venueId: '',
    request_type: 'export',
    full_name: '',
    email: '',
    phone: '',
    jurisdiction: '',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('');

    if (!form.full_name.trim() || !form.email.trim()) {
      setError('Full name and email are required.');
      return;
    }

    try {
      setSubmitting(true);
      const privacyApiBase = getPrivacyApiBase();
      if (!privacyApiBase) {
        throw new Error('Privacy request API is not configured.');
      }

      const res = await fetch(`${privacyApiBase}/privacy-requests/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venueId: form.venueId || undefined,
          request_type: form.request_type,
          full_name: form.full_name,
          email: form.email,
          phone: form.phone,
          jurisdiction: form.jurisdiction,
          message: form.message
        })
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const payload = await res.json();
      const reference = payload.request_ref || payload.request_id || 'submitted';
      setStatus(`Request submitted. Reference: ${reference}`);
      setForm({
        venueId: '',
        request_type: 'export',
        full_name: '',
        email: '',
        phone: '',
        jurisdiction: '',
        message: ''
      });
    } catch (err) {
      setError(err.message || 'Unable to submit privacy request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="privacy-page">
      <Header />
      <main className="privacy-main">
        <section className="privacy-card">
          <p className="privacy-kicker">Privacy Center</p>
          <h1>Data Export and Privacy Requests</h1>
          <p className="privacy-copy">
            Submit a request to export, delete, access, or correct personal data associated with your venue visits.
            A venue operator will review and respond by email.
          </p>

          <form onSubmit={submit} className="privacy-form">
            <label>
              Request Type
              <select name="request_type" value={form.request_type} onChange={onChange}>
                {REQUEST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>

            <label>
              Full Name *
              <input name="full_name" value={form.full_name} onChange={onChange} required />
            </label>

            <label>
              Email *
              <input name="email" value={form.email} onChange={onChange} type="email" required />
            </label>

            <label>
              Phone (optional)
              <input name="phone" value={form.phone} onChange={onChange} placeholder="+1..." />
            </label>

            <label>
              Venue ID (optional)
              <input name="venueId" value={form.venueId} onChange={onChange} placeholder="If known" />
            </label>

            <label>
              Jurisdiction (optional)
              <input name="jurisdiction" value={form.jurisdiction} onChange={onChange} placeholder="e.g., CA, EU, UK" />
            </label>

            <label>
              Details
              <textarea name="message" value={form.message} onChange={onChange} rows={4} placeholder="Tell us what data and timeframe this request covers." />
            </label>

            {error && <p className="privacy-error">{error}</p>}
            {status && <p className="privacy-success">{status}</p>}

            <button type="submit" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Request'}</button>
          </form>
        </section>
      </main>
    </div>
  );
}
